import React, { forwardRef } from 'react';
import { Switch } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

import { Neutrals, Primary } from '../../color-palette/colors';
import { FOCUS_RING, FOCUS_RING_OFFSET } from '../../focusRing';

import type { FieldBaseProps } from './field.types';
import { useFieldIds } from './useFieldIds';
import { describedBy } from './FieldShell';
import { BooleanFieldShell } from './BooleanFieldShell';
import type { BooleanFieldLayout } from './BooleanFieldShell';

export interface SwitchInputProps extends Omit<FieldBaseProps, 'hideLabel' | 'hideRequiredMark'> {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  layout?: BooleanFieldLayout;
  readOnly?: boolean;
}

const BASE = '& .MuiSwitch-switchBase';
const TRACK = '.MuiSwitch-track';

const switchSx = (theme: Theme): SystemStyleObject<Theme> => ({
  [BASE]: { color: theme.palette.background.paper },
  [`${BASE}.Mui-checked`]: { color: theme.palette.background.paper },
  [`& ${TRACK}`]: { backgroundColor: Neutrals[500], opacity: 1 },
  [`${BASE}.Mui-checked + ${TRACK}`]: { backgroundColor: Primary[500], opacity: 1 },
  [`${BASE}.Mui-disabled + ${TRACK}`]: { opacity: theme.palette.action.disabledOpacity },
  [`${BASE}.Mui-focusVisible .MuiSwitch-thumb`]: { outline: FOCUS_RING, outlineOffset: FOCUS_RING_OFFSET },
  '@media (prefers-reduced-motion: reduce)': { [`${BASE}, & ${TRACK}`]: { transition: 'none' } },
});

const holdValue = (event: React.MouseEvent<HTMLInputElement>): void => event.preventDefault();

export const SwitchInput = forwardRef<HTMLDivElement, SwitchInputProps>(function SwitchInput(
  {
    id: idProp, label, helperText, describedById, error, required, disabled, size = 'medium', fullWidth = true, className, sx,
    checked, defaultChecked, onChange, name, layout = 'inline', readOnly = false,
  },
  ref,
) {
  const ids = useFieldIds(idProp);
  return (
    <BooleanFieldShell
      ref={ref}
      ids={ids}
      label={label}
      helperText={helperText}
      error={error}
      required={required}
      disabled={disabled}
      fullWidth={fullWidth}
      className={className}
      sx={sx}
      layout={layout}
      control={
        <Switch
          id={ids.id}
          size={size}
          edge={layout === 'row' ? 'end' : false}
          checked={checked}
          defaultChecked={defaultChecked}
          name={name}
          onChange={(event, next) => {
            if (!readOnly) onChange?.(next, event);
          }}
          inputProps={{
            role: 'switch',
            'aria-describedby': describedBy(ids, helperText, describedById),
            'aria-readonly': readOnly || undefined,
            onClick: readOnly ? holdValue : undefined,
          }}
          sx={switchSx}
        />
      }
    />
  );
});
